import { useEffect, useRef } from 'react'
import type React from 'react'
import { type StationDetailsProps } from '@metro/station-details'
import {
  noopMetroAnalyticsReporter,
  type MetroAnalyticsReporter,
} from '../interaction/metroAnalyticsReporter'
import { StationDetailsWithAnalytics } from './StationDetailsWithAnalytics'
import { useSwipeDownDismiss } from './useSwipeDownDismiss'
import styles from './StationDetailsModal.module.css'

export interface StationDetailsModalProps {
  detailsProps: StationDetailsProps
  /**
   * Project analytics reporter. AppShell injects
   * `realMetroAnalyticsReporter` (forwards to GA4 +
   * Amplitude); forwarded into {@link StationDetailsWithAnalytics} so
   * the §8 events emit at runtime. Tests inject a spy. Optional — defaults
   * to the no-op reporter so direct unit-test renders stay silent.
   */
  reporter?: MetroAnalyticsReporter
  onClose: () => void
}

/**
 * Mobile-only iOS-style swipe-down sheet for <StationDetails>.
 * Triggered by the "Station information" tap on a StationCard carousel
 * slot when the inline (>= 768px) sidebar is unavailable. Mounted
 * directly in AppShell — no React Portal, since z-index management is
 * tractable at the AppShell level.
 *
 * Visual: partial-height sheet anchored to the bottom; the underlying
 * map peeks above (~10vh strip). Top-rounded corners + a small pill-
 * shaped drag handle as affordance.
 *
 * Dismiss: swipe-down gesture past 30% of sheet height (or velocity
 * > 0.6 px/ms), backdrop tap, or Escape key. The drag is implemented
 * with native PointerEvents (no dependency) on the sheet header zone
 * only — the content area below scrolls normally.
 *
 * TODO (a11y improvement deferred — single-modal app, low surface
 * area): add focus trap + return-focus-on-close + reduced-motion
 * fallback. When a second consumer appears, lift this primitive into
 * `@mammoth/design-kit` and pull in `react-focus-lock` (or equivalent)
 * at that point. Reduced-motion users still get the spring-back
 * transition; the open-from-bottom slide-in is also gated only by
 * `prefers-reduced-motion` in CSS.
 */
export function StationDetailsModal({
  detailsProps,
  reporter = noopMetroAnalyticsReporter,
  onClose,
}: StationDetailsModalProps): React.ReactElement {
  const sheetRef = useRef<HTMLDivElement | null>(null)
  const { springing, sheetStyle, handlers } = useSwipeDownDismiss(sheetRef, onClose)

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return (): void => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div
      className={styles.backdrop}
      data-testid="station-details-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={detailsProps.stationName}
      onClick={onBackdropClick}
    >
      <div
        ref={sheetRef}
        className={`${styles.sheet} ${springing ? styles.springing : ''}`}
        data-testid="station-details-modal-panel"
        style={sheetStyle}
      >
        <div
          className={styles.dragZone}
          data-testid="station-details-modal-drag-zone"
          {...handlers}
        >
          <div className={styles.handle} aria-hidden="true" />
        </div>
        <div className={styles.scrollArea}>
          <StationDetailsWithAnalytics {...detailsProps} reporter={reporter} />
        </div>
      </div>
    </div>
  )
}
